import React, { useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Badge } from '@/app/components/ui/badge'
import { Button } from '@/app/components/ui/button'
import { Textarea } from '@/app/components/ui/textarea'
import { DiscreteLoader } from '@/app/components/ui/discrete-loader'
import { toast } from 'sonner'
import z from 'zod'
import { useForm, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  InventoryTransfer,
  TransferListItem,
  TransferProductItem,
  transferService,
} from '@/app/services/transfer.service'

// ─── Validación (Zod) ────────────────────────────────────────────────────────
const formSchema = z.object({
  items: z.array(
    z.object({
      product_id: z.number(),
      product_name: z.string(),
      barcode: z.string().optional(),
      quantity: z.number(),
      requested_quantity: z.number(),
      approved_quantity: z.number(),
      response_type: z.enum(['FULL', 'PARTIAL', 'REJECTED']),
      response_reason: z.string().optional(),
      is_receivable: z.boolean(),
      status: z.enum(['RECEIVED', 'NOT_RECEIVED']),
      reason: z.string().optional()
    })
  ).min(1, 'La transferencia no tiene productos para revisar.')
}).superRefine((data, ctx) => {
  data.items.forEach((item, index) => {
    if (item.is_receivable && item.status === 'NOT_RECEIVED' && !item.reason?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'El motivo es obligatorio cuando el producto no fue recibido.',
        path: ['items', index, 'reason'],
      })
    }
  })
})

type FormValidate = z.infer<typeof formSchema>

interface ReceiveConfirmModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedTransfer: TransferListItem | null
  onConfirm: (transfer: TransferListItem | null, status: Record<string, never>) => void
}

export function ReceiveConfirmModal({
  open,
  onOpenChange,
  selectedTransfer,
  onConfirm,
}: ReceiveConfirmModalProps) {
  const realTransfer = selectedTransfer?._originalData as InventoryTransfer | undefined

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { isSubmitting },
  } = useForm<FormValidate>({
    mode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: {
      items: []
    }
  })

  const { fields } = useFieldArray({
    control,
    name: 'items'
  })

  const watchItems = watch('items')
  const receivableItemsCount = watchItems?.filter((item) => item.is_receivable).length || 0

  useEffect(() => {
    if (open && realTransfer) {
      const transferItems = realTransfer.items || []
      const hasReceivableItems = transferItems.some((item) => Number(item.approved_quantity || 0) > 0)
      const shouldShowRejectedContext = transferItems.length > 1 && hasReceivableItems

      const initialItems = transferItems
        .filter((item: TransferProductItem) => (
          Number(item.approved_quantity || 0) > 0 ||
          (shouldShowRejectedContext && item.response_type === 'REJECTED')
        ))
        .map((item: TransferProductItem) => ({
          product_id: Number(item.product_id),
          product_name: item.product?.name || `Producto #${item.product_id}`,
          barcode: item.product?.barcode || '',
          quantity: Number(item.approved_quantity || 0),
          requested_quantity: Number(item.requested_quantity || 0),
          approved_quantity: Number(item.approved_quantity || 0),
          response_type: item.response_type || 'FULL',
          response_reason: item.response_reason || '',
          is_receivable: Number(item.approved_quantity || 0) > 0,
          status: 'RECEIVED' as const,
          reason: ''
        }))
      reset({ items: initialItems })
    }
  }, [open, realTransfer, reset])

  const onSubmit = async (formData: FormValidate) => {
    try {
      if (!realTransfer) return

      const payload = {
        items: formData.items
          .filter(item => item.is_receivable)
          .map(item => ({
            product_id: item.product_id,
            status: item.status,
            reason: item.status === 'NOT_RECEIVED' ? item.reason : undefined
          }))
      }

      await transferService.receiveTransfer(realTransfer.id, payload)
      toast.success('Recepción confirmada exitosamente')

      onConfirm(selectedTransfer, {})
      onOpenChange(false)
    } catch (error: unknown) {
      const message = error && typeof error === 'object' && 'response' in error
        ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
        : null

      toast.error(message || 'Ocurrió un error al confirmar la recepción')
    }
  }

  const handleClose = () => {
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85vh] flex-col overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Confirmar Recepción de Transferencia</DialogTitle>
          <DialogDescription className="sr-only">
            Revisar y confirmar los productos recibidos de la transferencia
          </DialogDescription>
        </DialogHeader>
        {realTransfer && (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Detalles de la Transferencia */}
            <div className="rounded-lg bg-slate-50 p-4">
              <h4 className="mb-3 text-sm font-medium text-slate-900">
                Detalles de Recepción
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">ID de Recepción:</span>
                  <span className="font-medium text-slate-900">
                    RCP-{String(realTransfer.id).padStart(4, '0')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Fecha:</span>
                  <span className="font-medium text-slate-900">
                    {realTransfer.created_at?.split('T')[0]}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Desde:</span>
                  <span className="font-medium text-slate-900">
                    {realTransfer.origin_sucursal?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Hacia:</span>
                  <span className="font-medium text-slate-900">
                    {realTransfer.destination_sucursal?.name}
                  </span>
                </div>
              </div>
            </div>

            {/* Listado de Productos */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-slate-900">
                Productos a Recibir
              </h4>
              <p className="text-xs text-slate-600">
                Indica el estado de cada producto recibido
              </p>
              <div className="space-y-3">
                {fields.map((field, idx) => {
                  const currentItem = watchItems[idx]
                  if (!currentItem) return null

                  return (
                    <div
                      key={field.id}
                      className="space-y-3 rounded-lg border border-slate-200 p-3"
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium text-slate-900">
                            {currentItem.product_name}
                          </p>
                          <p className="text-xs text-slate-600">
                            {currentItem.barcode ? `CB: ${currentItem.barcode} | ` : ''}Solicitado: {currentItem.requested_quantity} unidades
                          </p>
                          <p className="text-xs text-slate-600">
                            Enviado: {currentItem.approved_quantity} unidades
                          </p>
                        </div>
                        <Badge
                          variant="outline"
                          className={
                            !currentItem.is_receivable
                              ? 'border-red-200 bg-red-50 text-red-700'
                              : currentItem.response_type === 'PARTIAL'
                                ? 'border-yellow-200 bg-yellow-50 text-yellow-700'
                                : currentItem.status === 'RECEIVED'
                                  ? 'border-green-200 bg-green-50 text-green-700'
                                  : 'border-red-200 bg-red-50 text-red-700'
                          }
                        >
                          {!currentItem.is_receivable && 'No enviado'}
                          {currentItem.is_receivable && currentItem.response_type === 'PARTIAL' && 'Parcial'}
                          {currentItem.is_receivable && currentItem.response_type !== 'PARTIAL' && currentItem.status === 'RECEIVED' && 'Recibido'}
                          {currentItem.is_receivable && currentItem.response_type !== 'PARTIAL' && currentItem.status === 'NOT_RECEIVED' && 'No recibido'}
                        </Badge>
                      </div>

                      {currentItem.response_type === 'PARTIAL' && currentItem.response_reason && (
                        <div className="rounded-md border border-yellow-200 bg-yellow-50 p-2 text-xs text-yellow-800">
                          <span className="font-medium">Motivo de envio parcial:</span>{' '}
                          {currentItem.response_reason}
                        </div>
                      )}

                      {!currentItem.is_receivable && currentItem.response_reason && (
                        <div className="rounded-md border border-red-200 bg-red-50 p-2 text-xs text-red-800">
                          <span className="font-medium">Motivo de no envio:</span>{' '}
                          {currentItem.response_reason}
                        </div>
                      )}

                      {currentItem.is_receivable && (
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setValue(`items.${idx}.status`, 'RECEIVED')
                            }}
                            className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${currentItem.status === 'RECEIVED'
                              ? 'border-green-500 bg-green-50 text-green-700'
                              : 'border-slate-200 hover:border-slate-300'
                              }`}
                          >
                            Recibido
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setValue(`items.${idx}.status`, 'NOT_RECEIVED')
                            }}
                            className={`rounded-lg border-2 p-2 text-xs font-medium transition-all ${currentItem.status === 'NOT_RECEIVED'
                              ? 'border-red-500 bg-red-50 text-red-700'
                              : 'border-slate-200 hover:border-slate-300'
                              }`}
                          >
                            No recibido
                          </button>
                        </div>
                      )}

                      {currentItem.is_receivable && currentItem.status === 'NOT_RECEIVED' && (
                        <div className="space-y-2">
                          <label className="text-xs font-medium text-slate-700">
                            Motivo (del remitente)
                          </label>
                          <Textarea
                            {...register(`items.${idx}.reason`)}
                            placeholder="Ingresa el motivo (ej. Faltante o dañado)..."
                            rows={2}
                            className="text-xs"
                          />
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Resumen */}
            <div className="rounded-lg border border-primary/20 bg-primary/10 p-4">
              <p className="mb-2 text-sm font-medium text-primary/90">
                Resumen de Recepción
              </p>
              <div className="space-y-1 text-sm text-primary">
                <p>
                  Productos recibidos:{' '}
                  {watchItems?.filter((v) => v.is_receivable && v.status === 'RECEIVED').length || 0}
                </p>
                {(watchItems?.filter((v) => v.is_receivable && v.status === 'NOT_RECEIVED').length || 0) > 0 && (
                  <p className="text-red-700">
                    Productos no recibidos:{' '}
                    {watchItems?.filter((v) => v.is_receivable && v.status === 'NOT_RECEIVED').length || 0}
                  </p>
                )}
                {(watchItems?.filter((v) => !v.is_receivable).length || 0) > 0 && (
                  <p className="text-red-700">
                    Productos no enviados:{' '}
                    {watchItems?.filter((v) => !v.is_receivable).length || 0}
                  </p>
                )}
              </div>
            </div>

            {/* Acciones */}
            <div className="flex gap-3">
              <Button type="submit" disabled={isSubmitting || receivableItemsCount === 0} className="flex-1">
                {isSubmitting ? <DiscreteLoader /> : 'Confirmar Recepción'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancelar
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
